import { INotificationMessage } from "@fl/shared/src/types/messages";
import { ConfiguracoesTokensAttributes } from "@fl/shared/src/types/models/suporte_fl/tokens";
import { IReturnHTTP } from "@fl/shared/src/types/returns";
import { ITableDataColumns } from "@fl/shared/src/types/tables";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  MdDelete,
  MdDeleteOutline,
  MdModeEdit,
  MdOutlineEdit,
} from "react-icons/md";
import HiddenText from "../../../components/hiddenText";
import TableData from "../../../components/tables";
import useConfirm from "../../../hooks/useConfirm";
import { tokensSchema } from "../../../schemas/tokens";
import ApiConfigTokens from "../../../services/suporte/auth/configs/tokens";
import DefaultBasePage from "../../default/base";

export type TokenFormInputs = ConfiguracoesTokensAttributes;

const ConfigTokensAssinaturasPrivatePage = () => {
  const columnsTable: ITableDataColumns[] = [
    {
      columnKey: "id_token",
      label: "#ID",
      pk: true,
      alignText: "text-center",
    },
    { columnKey: "empresa", label: "Empresa", alignText: "text-center" },
    {
      columnKey: "licenca",
      label: "Código da Licença",
      alignText: "text-center",
    },
    {
      columnKey: "app_token",
      label: "App Token",
      alignText: "text-center",
      render(item) {
        var token: string = item.app_token;
        return (
          <HiddenText
            text={token}
            firstLetters={5}
            bulletQuantity={token.length}
          />
        );
      },
    },
    {
      columnKey: "access_token",
      label: "Access Token",
      alignText: "text-center",
      render(item) {
        var token: string = item.access_token;
        return (
          <HiddenText
            text={token}
            firstLetters={5}
            bulletQuantity={token.length}
          />
        );
      },
    },
    {
      columnKey: "createdAt",
      label: "Data de Criação",
      dateColumns: true,
      alignText: "text-center",
    },
    {
      columnKey: "{actions}",
      label: "Ação",
      action: true,
      alignText: "text-center",
      actionButtons: [
        {
          label: "Editar Token",
          icon: <MdOutlineEdit size={15} />,
          iconHover: <MdModeEdit size={15} />,
          hover: false,
          action: (item) => onEditToken(item),
        },
        {
          label: "Excluir Token",
          icon: <MdDeleteOutline size={17.5} />,
          iconHover: <MdDelete size={17.5} />,
          hover: false,
          action: (item) => onDeleteToken(item),
        },
      ],
    },
  ];

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);

  const [tokens, setTokens] = useState<ConfiguracoesTokensAttributes[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<TokenFormInputs>({
    resolver: yupResolver(tokensSchema),
  });

  const id_token = watch("id_token");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const result = await new ApiConfigTokens().list();
    if (result.success) {
      setTokens(result.data);
    }
  };

  const onSubmit: SubmitHandler<TokenFormInputs> = async (data) => {
    try {
      setLoading(true);
      var result: IReturnHTTP;

      if (data.id_token) {
        result = await new ApiConfigTokens({ ...data }).update(
          data.id_token.toString()
        );
      } else {
        result = await new ApiConfigTokens({ ...data }).create();
      }

      if (result.message) setMessages(result.message);
      loadData();
      reset();
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    reset();
  };

  const onEditToken = (item: ConfiguracoesTokensAttributes) => {
    setValue("id_token", item.id_token ? item.id_token : 0);
    setValue("empresa", item.empresa ? item.empresa : "");
    setValue("licenca", item.licenca ? item.licenca : "");
    setValue("app_token", item.app_token ? item.app_token : "");
    setValue("access_token", item.access_token ? item.access_token : "");
  };

  const onDeleteToken = (item: ConfiguracoesTokensAttributes) => {
    handleShowGroup(
      "Confirmar exclusão de token?",
      `Tem certeza que deseja excluir o token com licença de "<b>${item.licenca}</b>" da "<b>${item.empresa}</b>"?`,
      item
    );
  };

  const submitDeleteToken = async (item: ConfiguracoesTokensAttributes) => {
    try {
      setLoading(true);
      const result = await new ApiConfigTokens({
        id_token: item.id_token,
      }).delete();

      if (result.success) {
        loadData();
        reset();
      }

      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  const { handleShow: handleShowGroup, ConfirmModalComponent: ConfirmModal } =
    useConfirm({
      onConfirm: submitDeleteToken,
    });

  return (
    <DefaultBasePage
      title="Tokens para Assinaturas"
      loading={loading}
      message={messages}
    >
      <div className="text-justify">
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Row className="mt-2">
            <Col className="mt-1">
              <Form.Group controlId="formEmpresa">
                <Form.Label>Empresa:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o empresa da empresa"
                  {...register("empresa")}
                  isInvalid={!!errors.empresa}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.empresa?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col className="mt-1">
              <Form.Group controlId="formLicenca">
                <Form.Label>Código Licença:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o código da licença"
                  {...register("licenca")}
                  isInvalid={!!errors.licenca}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.licenca?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col className="mt-1">
              <Form.Group controlId="formAppToken">
                <Form.Label>App Token:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o app token da licença"
                  {...register("app_token")}
                  isInvalid={!!errors.app_token}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.app_token?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col className="mt-1">
              <Form.Group controlId="formAccessToken">
                <Form.Label>Access Token:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o access token da licença"
                  {...register("access_token")}
                  isInvalid={!!errors.access_token}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.access_token?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-between">
            <Button
              variant="outline-danger"
              type="submit"
              className="mt-4"
              onClick={handleReset}
            >
              Limpar Dados
            </Button>
            <Button variant="outline-success" type="submit" className="mt-4">
              {id_token ? "Atualizar Dados" : "Salvar Dados"}
            </Button>
          </div>
        </Form>
      </div>

      <TableData columns={columnsTable} data={tokens} />

      {ConfirmModal}
    </DefaultBasePage>
  );
};

export default ConfigTokensAssinaturasPrivatePage;
